import type { InitialSyncProgress } from '../../lib/sync'
import { usePrefersReducedMotion } from '../../lib/usePrefersReducedMotion'
import GrainField from '../GrainField'
import Wordmark from '../Wordmark'
import {
  syncStatusLabel,
  syncProgressFraction,
  FAILED_TITLE,
  FAILED_BODY,
  STORE_FAILED_TITLE,
  STORE_FAILED_BODY,
} from './loadingCopy'
import styles from './AuthGate.module.css'

/**
 * QUIL — cold-start loading gate. Covers the app while the first sync of this
 * open settles, so content never flashes in half-synced and rearranges. The
 * status text is driven by *genuine* milestones from runInitialSync (files
 * pulled, stories found, work saved), with an indeterminate "still walking"
 * ticker only where there's no per-item signal.
 *
 * A11y: the whole animated block is aria-hidden and the region carries one
 * stable aria-label, so the live-updating count never spams a screen reader.
 * Under prefers-reduced-motion the ticker settles instead of animating.
 */
export function LoadingGate({ progress }: { progress: InitialSyncProgress | null }) {
  const reduced = usePrefersReducedMotion()
  const label = syncStatusLabel(progress)
  const fraction = syncProgressFraction(progress)
  const determinate = fraction !== null

  return (
    // id/tabIndex keep the shell's skip-link target (#main) alive during the
    // cold-start barrier too.
    <div id="main" tabIndex={-1} className={styles.splash} role="status" aria-label="Retrieving the library">
      <GrainField />
      <div className={styles.splashStack} aria-hidden="true">
        <Wordmark className={styles.splashWordmark} />
        <div className={styles.loadLabel}>{label}</div>
        <div
          className={styles.trail}
          data-determinate={determinate ? 'true' : 'false'}
          data-motion={reduced ? 'reduced' : 'full'}
        >
          <span
            className={styles.trailFill}
            style={determinate ? { transform: `scaleX(${fraction ?? 0})` } : undefined}
          />
        </div>
      </div>
    </div>
  )
}

/**
 * Shown only when the cold-start sync failed AND there's no local library to
 * fall back to — an empty app would look broken, so we say so honestly and
 * offer a retry or an explicit "keep writing offline". Interactive, so it is
 * NOT aria-hidden.
 */
export function FailedGate({
  onRetry,
  onContinue,
}: {
  onRetry: () => void
  onContinue: () => void
}) {
  return (
    // id/tabIndex keep the skip-link target alive on the failure gate too.
    <main id="main" tabIndex={-1} className={styles.signin}>
      <GrainField />
      <div className={styles.signinInner}>
        <Wordmark className={styles.wordmark} />
        <div className={styles.failTitle}>{FAILED_TITLE}</div>
        <p className={styles.lede}>{FAILED_BODY}</p>
        <div className={styles.failActions}>
          <button
            className={`${styles.btn} ${styles.btnPrimary}`}
            type="button" onClick={onRetry} data-testid="loadgate-retry"
          >
            Try again
          </button>
          <button
            className={`${styles.btn} ${styles.btnGhost}`}
            type="button" onClick={onContinue} data-testid="loadgate-offline"
          >
            Keep writing offline
          </button>
        </div>
      </div>
    </main>
  )
}

/**
 * Shown when this device's library could not be opened (W12 A5): the account could not be matched to
 * its local store, or the store could not be upgraded. Nothing was changed, adopted or deleted, so
 * the only honest thing to offer is another try: there is no store to "keep writing" against, and
 * quietly opening a fresh one would hide the writer's own library from them. Interactive, so it is
 * NOT aria-hidden.
 */
export function StoreFailedGate({ onRetry }: { onRetry: () => void }) {
  return (
    // id/tabIndex keep the skip-link target alive on this gate too.
    <main id="main" tabIndex={-1} className={styles.signin}>
      <GrainField />
      <div className={styles.signinInner}>
        <Wordmark className={styles.wordmark} />
        <div className={styles.failTitle} data-testid="storegate-title">{STORE_FAILED_TITLE}</div>
        <p className={styles.lede}>{STORE_FAILED_BODY}</p>
        <div className={styles.failActions}>
          <button
            className={`${styles.btn} ${styles.btnPrimary}`}
            type="button" onClick={onRetry} data-testid="storegate-retry"
          >
            Try again
          </button>
        </div>
      </div>
    </main>
  )
}
